// import { isUserLoggedIn } from '../auth/auth.js'; // <-- Will be injected

// --- 模块作用域变量 ---
let chatMessagesContainer = null;
let chatAttachBtn = null;
let chatImageUpload = null;
let chatTextInput = null;
let chatSendBtn = null;
let chatImagePreview = null;
let previewImage = null;
let removeImageBtn = null;
let chatConfirmationArea = null;
let chatConfirmYesBtn = null;
let chatConfirmNoBtn = null;

let currentConversationId = null;
let isAwaitingConfirmation = false;
let onConfirmCallback = async (conversationId, confirmed) => {}; // Callback for confirmation/correction
let onCorrectionInputNeededCallback = () => {}; // Callback when correction button is clicked

let selectedImageFile = null;
let onSendMessageCallback = (text, imageFile) => {}; // 发送回调占位符
let isUserLoggedInFunc = () => false; // Placeholder for injected function

// --- DOM 操作 & 辅助函数 ---

/**
 * 获取并缓存聊天相关的 DOM 元素。
 * @returns {boolean} 如果所有必要元素都找到则返回 true。
 */
function getChatDOMElements() {
    chatMessagesContainer = document.getElementById('chat-messages');
    chatAttachBtn = document.getElementById('chat-attach-btn');
    chatImageUpload = document.getElementById('chat-image-upload');
    chatTextInput = document.getElementById('chat-text-input');
    chatSendBtn = document.getElementById('chat-send-btn');
    chatImagePreview = document.getElementById('chat-image-preview');
    previewImage = document.getElementById('preview-image');
    removeImageBtn = document.getElementById('remove-image-btn');
    chatConfirmationArea = document.getElementById('chat-confirmation-area');
    chatConfirmYesBtn = document.getElementById('chat-confirm-yes-btn');
    chatConfirmNoBtn = document.getElementById('chat-confirm-no-btn');

    // 检查核心元素是否存在
    if (!chatMessagesContainer || !chatTextInput || !chatSendBtn || !chatAttachBtn || !chatImageUpload || !chatConfirmationArea || !chatConfirmYesBtn || !chatConfirmNoBtn) {
        console.error('[Chat UI] Essential chat or confirmation elements not found!');
        return false;
    }
    return true;
}

/** Handles the selection of a file (image) */
function handleFileSelect(event) {
    const files = event.target.files;
    if (!files || files.length === 0) {
        removePreviewImage();
        return;
    }

    const file = files[0];
    if (!file.type.startsWith('image/')) {
        addChatMessage('请选择一个图片文件。' , 'system');
        removePreviewImage();
        return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
        addChatMessage('图片文件大小不能超过 5MB。' , 'system');
        removePreviewImage();
        return;
    }

    selectedImageFile = file;

    const reader = new FileReader();
    reader.onload = (e) => {
        if (previewImage && typeof e.target?.result === 'string') {
            previewImage.src = e.target.result;
            chatImagePreview?.classList.remove('hidden');
            if (chatTextInput) chatTextInput.placeholder = "图片已选择，可直接发送或添加文字描述";
        } else {
            console.error("[Chat UI] Error reading file or setting preview src.");
            removePreviewImage();
            addChatMessage('无法预览此图片。', 'system');
        }
    };
    reader.onerror = (e) => {
         console.error("[Chat UI] FileReader error:", e);
         removePreviewImage();
         addChatMessage('无法读取图片文件。', 'system');
    };
    reader.readAsDataURL(file); // Read file as Data URL for preview
}

/** Dynamically adjusts the height of the text input based on content */
function handleTextInputSize() {
    if(!chatTextInput) return;
    chatTextInput.style.height = 'auto';
    const maxHeight = 150;
    const scrollHeight = chatTextInput.scrollHeight;
    chatTextInput.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    chatTextInput.style.overflowY = scrollHeight > maxHeight ? 'scroll' : 'hidden';
}

/** Handles Enter key press in the text input */
function handleEnterSend(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        triggerSend(); // Call internal send trigger
    }
}

/**
 * Internal function to handle the send action.
 * Collects input, displays user message, clears input, and calls the appropriate callback.
 * Handles both initial messages and correction messages during confirmation.
 */
function triggerSend() {
    const textInput = chatTextInput.value.trim();
    const imageFile = selectedImageFile; // Use the module-scoped variable

    if (isAwaitingConfirmation) {
        if (!textInput) {
             addChatMessage('如果您想修正信息，请在此输入。', 'system');
             focusChatInput();
            return;
        }
        console.log('[Chat UI] Sending correction for conversation:', currentConversationId);
        // User provided correction text
        addChatMessage(textInput, 'user'); // Display user's correction
        const conversationToSend = currentConversationId;
        toggleConfirmationArea(false); // Hide buttons after sending correction
        toggleSendButtonLoading(true);
        disableChatInput(true, "正在发送您的修正...");
        chatTextInput.value = '';
        handleTextInputSize();

        // Call the confirmation callback with confirmed=false and the text
        // Wrap in Promise.resolve in case the callback itself isn't async
        Promise.resolve(onConfirmCallback(conversationToSend, false, textInput)).catch(err => {
             console.error("[Chat UI] Error during correction callback:", err);
             addChatMessage(`发送修正时出错: ${err.message}`, 'system');
        }).finally(() => {
             // Re-enable input regardless of callback outcome (handled within callback ideally)
             toggleSendButtonLoading(false); 
             disableChatInput(false);
        });
        return; // Stop here, don't process as a new message
    }

    if (!textInput && !imageFile) {
        console.log("[Chat UI] Send triggered, but no text or image.");
        chatTextInput.focus();
        return;
    }

    // --- Display User Message Immediately ---
    let userMessageText = textInput;
    let previewUrl = null;
    if (imageFile) {
        previewUrl = URL.createObjectURL(imageFile);
    }
    addChatMessage(userMessageText, 'user', previewUrl);

    // --- Clear Input & Reset UI State ---
    const fileToSend = imageFile; // Keep reference to the file being sent
    chatTextInput.value = '';
    removePreviewImage(); // Clears module's selectedImageFile and UI
    handleTextInputSize();
    toggleSendButtonLoading(true); // Show loading
    disableChatInput(true, "正在处理您的请求...");

    // --- Call the Send Callback ---
    console.log(`[Chat UI] Calling onSendMessageCallback... Text: ${textInput ? 'Yes' : 'No'}, Image: ${fileToSend ? 'Yes' : 'No'}`);
    let callbackPromise;
    try {
        // Execute the callback and store the promise it returns
        callbackPromise = Promise.resolve(onSendMessageCallback(textInput, fileToSend)); 
    } catch (error) {
        console.error("[Chat UI] Error executing onSendMessageCallback synchronously:", error);
        addChatMessage(`处理请求时出错: ${error.message}`, 'system');
        toggleSendButtonLoading(false);
        disableChatInput(false);
        callbackPromise = Promise.reject(error); 
    }

    // --- Revoke URL only AFTER the callback promise settles ---
    callbackPromise.finally(() => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            console.log("[Chat UI] Revoked object URL after callback finished:", previewUrl);
        }
        // Input re-enabling should be handled by the logic processing the API response
    });
}

/** Binds event listeners to the chat input elements */
function bindChatInputEvents() {
    if (!chatAttachBtn || !chatImageUpload || !removeImageBtn || !chatTextInput || !chatSendBtn || !chatConfirmYesBtn || !chatConfirmNoBtn) {
        console.error("[Chat UI] Cannot bind events: one or more chat or confirmation elements missing.");
        return;
    }
    chatAttachBtn.addEventListener('click', () => chatImageUpload.click());
    chatImageUpload.addEventListener('change', handleFileSelect);
    removeImageBtn.addEventListener('click', removePreviewImage);
    chatTextInput.addEventListener('input', handleTextInputSize);
    chatSendBtn.addEventListener('click', triggerSend);
    chatTextInput.addEventListener('keydown', handleEnterSend);

    chatConfirmYesBtn.addEventListener('click', () => {
         if (!isAwaitingConfirmation || !currentConversationId) return;
         console.log('[Chat UI] Confirm Yes clicked for conversation:', currentConversationId);
         const conversationToSend = currentConversationId;
         toggleConfirmationArea(false); // Hide buttons immediately
         toggleSendButtonLoading(true); // Show loading on main send button
         disableChatInput(true, "正在确认...");

         // Call the confirmation callback with confirmed=true
         Promise.resolve(onConfirmCallback(conversationToSend, true, null)).catch(err => {
              console.error("[Chat UI] Error during confirmation callback:", err);
              addChatMessage(`确认时出错: ${err.message}`, 'system');
         }).finally(() => {
              // Re-enable input (handled within callback ideally)
              toggleSendButtonLoading(false); 
              disableChatInput(false); 
         });
    });

    chatConfirmNoBtn.addEventListener('click', () => {
         if (!isAwaitingConfirmation) return;
         console.log('[Chat UI] Confirm No (Correction) clicked.');
         addChatMessage('好的，请在下方输入框中输入您想要修正的内容，然后按发送。' , 'system');
         focusChatInput();
         // Optionally, call a specific callback if the main controller needs to know
         onCorrectionInputNeededCallback();
         // Buttons remain visible until user sends correction via text input
    });

    console.log("[Chat UI] Event listeners bound.");
}

// --- Public API ---

/**
 * Initializes the chat UI module.
 * Gets DOM elements, binds events, and sets up callbacks.
 * @param {object} options
 * @param {function(text: string, imageFile: File | null): Promise<void>} options.onSend - Async callback when user sends a new message.
 * @param {function(conversationId: string, confirmed: boolean, correctionText: string | null): Promise<void>} options.onConfirm - Async callback when user confirms or provides correction.
 * @param {function(): void} [options.onCorrectionNeeded] - Optional callback when user clicks 'No/Correct'.
 * @param {function(): boolean} options.isUserLoggedIn - Function to check auth status.
 */
export function initChat({ onSend, onConfirm, onCorrectionNeeded, isUserLoggedIn }) {
    console.log('[Chat UI] Initializing...');
    if (!getChatDOMElements()) {
        return;
    }

    // Store injected dependencies
    onSendMessageCallback = onSend || onSendMessageCallback;
    isUserLoggedInFunc = isUserLoggedIn || isUserLoggedInFunc;
    onConfirmCallback = onConfirm || onConfirmCallback;
    onCorrectionInputNeededCallback = onCorrectionNeeded || onCorrectionInputNeededCallback;

    bindChatInputEvents();

    // Initial state
    const initialPlaceholder = isUserLoggedInFunc() ? "在此输入订阅信息或描述..." : "请先登录以添加或管理订阅";
    disableChatInput(false, initialPlaceholder);
    removePreviewImage();
    toggleConfirmationArea(false); // Ensure confirmation area is hidden initially

    console.log('[Chat UI] Initialization complete.');
}

/**
 * Appends a chat message to the chat message display area.
 * @param {string} text - The message text.
 * @param {'user' | 'assistant' | 'system'} sender - Who sent the message.
 * @param {string | null} imageUrl - Optional URL of an image associated with the message.
 */
export function addChatMessage(text, sender = 'user', imageUrl = null) {
    if (!chatMessagesContainer) {
        console.error("[Chat UI] Chat messages container not found!");
        return;
    }

    const messageDiv = document.createElement('div');
    messageDiv.classList.add('chat-message', sender);

    if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = "聊天图片附件"; // More specific alt text
        img.classList.add('chat-image-attachment');
        messageDiv.appendChild(img);
    }

    if (text) {
        const textNode = document.createElement('p');
        // Simple text setting for now
        textNode.textContent = text;
        messageDiv.appendChild(textNode);
    }

    chatMessagesContainer.appendChild(messageDiv);
    chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;

    console.log(`[Chat UI] Added ${sender} message:`, text ? text.substring(0, 50) + '...' : '(Image only)');
}

/** Clears the selected image file and hides the preview */
export function removePreviewImage() {
    selectedImageFile = null;
    if (chatImageUpload) chatImageUpload.value = '';
    chatImagePreview?.classList.add('hidden');
    if (previewImage) previewImage.src = '#';

    if (chatTextInput) {
        // Update placeholder based on current login state
        chatTextInput.placeholder = isUserLoggedInFunc()
            ? "在此输入订阅信息或描述..."
            : "请先登录以添加或管理订阅";
    }
}

/** Disables or enables the chat input area elements */
export function disableChatInput(disabled, placeholderText = null) {
    if (chatTextInput) {
        chatTextInput.disabled = disabled;
        if (placeholderText !== null) {
            chatTextInput.placeholder = placeholderText;
        }
    }
    if (chatAttachBtn) chatAttachBtn.disabled = disabled;
    if (chatSendBtn) chatSendBtn.disabled = disabled;
    if (removeImageBtn) removeImageBtn.disabled = disabled;
    console.log(`[Chat UI] Input ${disabled ? 'disabled' : 'enabled'}.`);
}

/** Helper to show/hide loading spinner on send button */
export function toggleSendButtonLoading(isLoading) {
     if (!chatSendBtn) return;
     const sendIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M2,21L23,12L2,3V10L17,12L2,14V21Z"></path></svg>';
     const spinnerSpan = '<span class="spinner"></span>';

     if (isLoading) {
         chatSendBtn.innerHTML = spinnerSpan;
         chatSendBtn.disabled = true;
         // Also disable other inputs when loading
         disableChatInput(true, "正在处理...");
     } else {
         chatSendBtn.innerHTML = sendIconSvg;
         // Always re-enable input when loading finishes
         const placeholder = isUserLoggedInFunc() ? "在此输入订阅信息或描述..." : "未登录：输入信息将本地保存";
         disableChatInput(false, placeholder);
     }
}

/**
 * Focuses the chat text input element.
 */
export function focusChatInput() {
    chatTextInput?.focus();
}

/**
 * Shows or hides the confirmation buttons and stores conversation state.
 * Should be called by the main controller after receiving 'awaiting_confirmation' status.
 * @param {boolean} show - Whether to show the confirmation area.
 * @param {string | null} [conversationId] - The conversation ID (required if show is true).
 */
export function toggleConfirmationArea(show, conversationId = null) {
     console.log(`[Chat UI] Toggling confirmation area: ${show}, ConvID: ${conversationId}`);
     if (show) {
         if (!conversationId) {
             console.error('[Chat UI] Cannot show confirmation area without a conversation ID.');
             return;
         }
         currentConversationId = conversationId;
         isAwaitingConfirmation = true;
         chatConfirmationArea?.classList.remove('hidden');
         disableChatInput(true, "请确认上方信息或输入修正内容..."); // Disable direct input while confirming with buttons
     } else {
         currentConversationId = null;
         isAwaitingConfirmation = false;
         chatConfirmationArea?.classList.add('hidden');
         // Re-enable input (placeholder logic might need adjustment based on login state)
         const placeholder = isUserLoggedInFunc() ? "在此输入订阅信息或描述..." : "请先登录以添加或管理订阅";
         disableChatInput(false, placeholder);
     }
 }

/**
 * Checks if the chat UI is currently waiting for user confirmation.
 * @returns {boolean}
 */
export function isChatAwaitingConfirmation() {
    return isAwaitingConfirmation;
}
